import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { Modal } from 'antd';
import type { FlowNodeEntity } from '@flowgram.ai/free-layout-editor';
import classNames from 'classnames';
import {
  createMediaLibraryFolder,
  getMediaLibraryFolderTree,
  getMediaLibraryFolderMoveStatus,
  moveMediaLibraryFolder,
  isAssetLibraryApiMessageShown,
  StorageExceededError,
} from '$/api/canvasRquest';
import type {
  MediaLibraryFolderTreeNode,
  MediaLibrarySystemKey,
  TeamStorageInfoResponse,
} from '$/api/server.types';
import Button from '$/components/base/Button';
import IconFont from '$/components/IconFont';
import Input from '$/components/base/Input';
import { message } from '$/components/Message';
import {
  getTeamFlowNodeDefaultMediaLibraryName,
  saveTeamFlowNodeToMediaLibrary,
} from '$/pages/RoboneoTeamFlow/utils/saveNodeToMediaLibrary';
import { trackSaveAssetUpload } from '$/pages/RoboneoTeamFlow/utils/flowNodeTrack';
import { TVFlowNodeNextTypeEnum } from '$/pages/RoboneoTeamFlow/config/node.config';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import styles from './index.module.scss';

/** 宿主容量拦截弹窗展示的待上传资产项。 */
export interface UploadAssetItem {
  id: string;
  name: string;
  type: string;
  format: string;
  thumbnail?: string;
  isTeamShared?: boolean;
  variant?: 'default' | 'landscape' | 'portrait' | 'audio';
}

/** SDK 保存资产命中容量不足时抛给宿主的拦截信息。 */
export interface AssetStorageExceededPayload {
  scene: string;
  requiredBytes: number;
  uploadAssets?: UploadAssetItem[];
  storageInfo?: TeamStorageInfoResponse;
}

export type AssetStorageExceededHandler = (payload: AssetStorageExceededPayload) => void;

interface SaveToMediaLibraryModalProps {
  node?: FlowNodeEntity;
  initialName?: string;
  onClose: () => void;
  onSubmit?: (options: { folderId: number; name: string; }) => Promise<void>;
  previewInfo?: { icon: string; url: string; };
  initialFolderSystemKey?: MediaLibrarySystemKey;
  zIndex?: number;
  /** 隐藏资产名称字段（多选批量保存场景，各资源默认使用节点名称）。 */
  hideName?: boolean;
}

interface FlatFolder extends MediaLibraryFolderTreeNode {
  level: number;
}

const MAX_NAME_LENGTH = 20;
const FOLDER_MOVE_POLL_INTERVAL_MS = 1500;
const FOLDER_MOVE_POLL_MAX_ATTEMPTS = 40;
let assetStorageExceededHandler: AssetStorageExceededHandler | undefined;

/** 注册团队工作流保存资产容量拦截回调，供宿主在应用层渲染容量弹窗。 */
export function setTeamFlowAssetStorageExceededHandler(handler?: AssetStorageExceededHandler) {
  assetStorageExceededHandler = handler;
}

/** 轮询等待文件夹移动异步任务完成，与资产库主流程一致。 */
async function waitForFolderMoveTask(taskId: string): Promise<void> {
  for (let i = 0; i < FOLDER_MOVE_POLL_MAX_ATTEMPTS; i += 1) {
    // eslint-disable-next-line no-await-in-loop -- 需按间隔顺序轮询任务状态
    const result = await getMediaLibraryFolderMoveStatus(taskId);
    if (result.status === 'done') return;
    if (result.status === 'failed') return;
    // eslint-disable-next-line no-await-in-loop -- 轮询间隔
    await new Promise((resolve) => { setTimeout(resolve, FOLDER_MOVE_POLL_INTERVAL_MS); });
  }
}

/**
 * 新建文件夹后移动到 sort_order 1，让新文件夹排在最前（与资产库主流程一致）。
 * 移动失败不阻断创建结果，仅记录告警。
 */
async function moveCreatedFolderToTop(folderId: number, parentId: number): Promise<void> {
  try {
    const moveResult = await moveMediaLibraryFolder({
      folder_id: folderId,
      target_parent_id: parentId,
      sort_order: 1,
    });
    if (moveResult?.async && moveResult.task_id) {
      await waitForFolderMoveTask(moveResult.task_id);
    }
  } catch (moveErr) {
    // eslint-disable-next-line no-console
    console.warn('[SaveToMediaLibraryModal] move created folder to top failed', moveErr);
  }
}

/** 读取字符串字段并去除首尾空白。 */
function pickString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

/** 将字符串按 rune 截断到素材库名称上限。 */
function truncateByRune(value: string, maxLength = MAX_NAME_LENGTH): string {
  return Array.from(value).slice(0, maxLength).join('');
}

/** 扁平化文件夹树，供下拉菜单渲染。 */
function flattenFolderTree(list: MediaLibraryFolderTreeNode[], level = 0): FlatFolder[] {
  return list.flatMap((folder) => {
    const current: FlatFolder = { ...folder, level };
    return [
      current,
      ...flattenFolderTree(folder.children ?? [], level + 1),
    ];
  });
}

/** 收集存在子文件夹的文件夹 id，用于保存位置下拉展示展开箭头。 */
function collectExpandableFolderIds(list: MediaLibraryFolderTreeNode[]): Set<number> {
  const ids = new Set<number>();
  list.forEach((folder) => {
    if (folder.children?.length) {
      ids.add(Number(folder.id));
      collectExpandableFolderIds(folder.children).forEach((id) => ids.add(id));
    }
  });
  return ids;
}

/** 按当前展开状态扁平化文件夹树，未展开节点的子级不进入下拉列表。 */
function flattenVisibleFolderTree(
  list: MediaLibraryFolderTreeNode[],
  expandedFolderIds: Set<number>,
  level = 0,
): FlatFolder[] {
  return list.flatMap((folder) => {
    const current: FlatFolder = { ...folder, level };
    if (!expandedFolderIds.has(Number(folder.id))) return [current];
    return [
      current,
      ...flattenVisibleFolderTree(folder.children ?? [], expandedFolderIds, level + 1),
    ];
  });
}

/** 按 parent_id 找到同层文件夹，用于前端同名预校验。 */
function getSiblingFolders(list: MediaLibraryFolderTreeNode[], parentId: number): MediaLibraryFolderTreeNode[] {
  if (parentId === 0) return list;
  for (const folder of list) {
    if (Number(folder.id) === Number(parentId)) {
      return folder.children ?? [];
    }
    const children = getSiblingFolders(folder.children ?? [], parentId);
    if (children.length) return children;
  }
  return [];
}

/** 校验当前或新建文件夹 id 是否仍存在于最新文件夹树。 */
function pickExistingFolderId(flatFolders: FlatFolder[], preferredId?: number | null): number | null {
  if (preferredId && flatFolders.some((folder) => Number(folder.id) === Number(preferredId))) {
    return preferredId;
  }
  return null;
}

/** 按系统文件夹 key 查找真实目录 id，供指定业务默认保存位置。 */
function findFolderIdBySystemKey(
  flatFolders: FlatFolder[],
  systemKey?: MediaLibrarySystemKey,
): number | null {
  if (!systemKey) return null;
  const folder = flatFolders.find((item) => item.system_key === systemKey);
  return folder ? Number(folder.id) : null;
}

/** 从当前节点中提取保存弹窗左侧预览内容，图片/视频优先使用可展示 URL。 */
function getMediaLibraryPreviewInfo(node: FlowNodeEntity) {
  const data = (node.toJSON?.()?.data ?? {}) as { media_list?: Record<string, any>[]; };
  const firstMedia = Array.isArray(data.media_list) ? data.media_list[0] ?? {} : {};
  const nodeType = node.flowNodeType;
  const coverUrl = pickString(
    firstMedia.thumbnail_url,
    firstMedia.thumbnailUrl,
    firstMedia.cover_url,
    firstMedia.coverUrl,
    firstMedia.watermark_cover_url,
    firstMedia.watermarkCoverUrl,
  );
  const imageUrl = pickString(
    coverUrl,
    firstMedia.watermark_url,
    firstMedia.watermarkUrl,
    firstMedia.originUrl,
    firstMedia.url,
    firstMedia.uri,
  );

  if (nodeType === TVFlowNodeNextTypeEnum.AUDIO_NODE) {
    return { icon: 'musicV3Bold', url: '' };
  }
  if (nodeType === TVFlowNodeNextTypeEnum.TEXT_NODE || nodeType === TVFlowNodeNextTypeEnum.SCRIPT_NODE) {
    return { icon: 'fileTextBold', url: '' };
  }
  if (nodeType === TVFlowNodeNextTypeEnum.VIDEO_NODE) {
    return { icon: 'videoFileBold', url: coverUrl };
  }
  if (nodeType === TVFlowNodeNextTypeEnum.IMAGE_NODE) {
    return { icon: 'pictureBold', url: imageUrl };
  }
  return { icon: 'fileBold', url: imageUrl };
}

/** 将保存弹窗预览图标映射为容量拦截弹窗展示的素材类型。 */
function getStorageExceededAssetType(icon: string): string {
  if (icon === 'musicV3Bold') return '音色';
  if (icon === 'videoFileBold') return '视频';
  if (icon === 'fileTextBold') return '文本';
  return '角色';
}

/** 将保存弹窗预览图标映射为容量拦截弹窗展示的文件格式。 */
function getStorageExceededAssetFormat(icon: string): string {
  if (icon === 'musicV3Bold') return 'MP3';
  if (icon === 'videoFileBold') return 'MP4';
  return 'PNG';
}

/** 将保存弹窗预览图标映射为容量拦截弹窗需要的卡片变体。 */
function getStorageExceededAssetVariant(icon: string): UploadAssetItem['variant'] {
  return icon === 'musicV3Bold' ? 'audio' : 'default';
}

/** 处理素材库保存弹窗的表单、文件夹树和提交。 */
function SaveToMediaLibraryModal({
  initialName,
  node,
  onClose,
  onSubmit,
  previewInfo: customPreviewInfo,
  initialFolderSystemKey,
  zIndex = 600,
  hideName = false,
}: SaveToMediaLibraryModalProps) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const unnamedFolderText = languageStore.smartcatTranslationText((value) => value.unnamed_folder);
  const loadSubscribeInfo = useOfficialStore((state) => state.loadSubscribeInfo);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedFolderIdRef = useRef<number | null>(null);
  const [assetName, setAssetName] = useState(() => (
    initialName?.trim() || (node ? getTeamFlowNodeDefaultMediaLibraryName(node) : '')
  ));
  const [folders, setFolders] = useState<MediaLibraryFolderTreeNode[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [folderDropdownOpen, setFolderDropdownOpen] = useState(false);
  const [folderLoading, setFolderLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [creatingParentId, setCreatingParentId] = useState<number | null>(null);
  const [creatingName, setCreatingName] = useState('');
  const [creating, setCreating] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(true);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<number>>(() => new Set());

  const flatFolders = useMemo(() => flattenFolderTree(folders), [folders]);
  const expandableFolderIds = useMemo(() => collectExpandableFolderIds(folders), [folders]);
  const visibleFolders = useMemo(
    () => flattenVisibleFolderTree(folders, expandedFolderIds),
    [expandedFolderIds, folders],
  );
  const selectedFolder = useMemo(
    () => flatFolders.find((folder) => Number(folder.id) === Number(selectedFolderId)),
    [flatFolders, selectedFolderId],
  );
  const previewInfo = useMemo(() => (
    customPreviewInfo || (node ? getMediaLibraryPreviewInfo(node) : { icon: 'fileBold', url: '' })
  ), [customPreviewInfo, node]);
  const saveDisabled = submitting || folderLoading || (!hideName && !assetName.trim()) || !selectedFolderId;

  /** 从后端加载文件夹树，优先保留指定目录，首次进入可按系统 key 默认选中。 */
  const loadFolders = useCallback(async (preferredId?: number | null) => {
    setFolderLoading(true);
    try {
      const res = await getMediaLibraryFolderTree();
      const nextFolders = Array.isArray(res.list) ? res.list : [];
      const nextFlatFolders = flattenFolderTree(nextFolders);
      const nextSelectedId = pickExistingFolderId(
        nextFlatFolders,
        preferredId ?? selectedFolderIdRef.current,
      ) ?? findFolderIdBySystemKey(nextFlatFolders, initialFolderSystemKey);
      setFolders(nextFolders);
      setExpandedFolderIds(collectExpandableFolderIds(nextFolders));
      setSelectedFolderId(nextSelectedId);
      selectedFolderIdRef.current = nextSelectedId;
    } catch (err) {
      if (!isAssetLibraryApiMessageShown(err)) {
        message.error(languageStore.smartcatTranslationText((v) => v.assets_get_list_fail));
      }
    } finally {
      setFolderLoading(false);
    }
  }, [initialFolderSystemKey, languageStore]);

  /** 创建文件夹前做空名称、长度和同级重名校验。 */
  const validateCreateFolderName = useCallback((parentId: number, value: string) => {
    const name = value.trim();
    if (!name) {
      message.error(languageStore.smartcatTranslationText((v) => v.assets_created_folder_error));
      return '';
    }
    if (Array.from(name).length > MAX_NAME_LENGTH) {
      message.error(`文件夹名称最多 ${MAX_NAME_LENGTH} 字`);
      return '';
    }
    const siblings = getSiblingFolders(folders, parentId);
    if (siblings.some((folder) => folder.name === name)) {
      message.error(languageStore.smartcatTranslationText((v) => v['256']));
      return '';
    }
    return name;
  }, [folders, languageStore]);

  /** 提交新建文件夹，并自动选中新建结果。 */
  const submitCreateFolder = useCallback(async () => {
    if (creatingParentId == null || creating) return;
    const parentId = creatingParentId;
    const name = validateCreateFolderName(parentId, creatingName);
    if (!name) return;
    setCreating(true);
    try {
      const { folder_id: folderId } = await createMediaLibraryFolder({
        parent_id: parentId,
        name,
      });
      await moveCreatedFolderToTop(Number(folderId), parentId);
      setCreatingName('');
      setCreatingParentId(null);
      await loadFolders(folderId);
    } catch (err) {
      if (!isAssetLibraryApiMessageShown(err)) {
        message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
      }
    } finally {
      setCreating(false);
    }
  }, [creating, creatingName, creatingParentId, languageStore, loadFolders, validateCreateFolderName]);

  /** 切换保存位置下拉里的文件夹展开状态，不改变当前保存目标。 */
  const toggleFolderExpanded = useCallback((folderId: number) => {
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  /** 点击保存时校验名称和分组，再提交到 media_library/item/save.json。 */
  const submitSave = useCallback(async () => {
    if (submitting) return;
    const name = assetName.trim();
    if (!hideName) {
      if (!name) {
        message.error(languageStore.smartcatTranslationText((v) => v.enter_name));
        return;
      }
      if (Array.from(name).length > MAX_NAME_LENGTH) {
        message.error(`名称最多 ${MAX_NAME_LENGTH} 字`);
        return;
      }
    }
    if (!selectedFolderId) {
      message.error(languageStore.smartcatTranslationText((v) => v.please_select_a_folder));
      return;
    }
    setSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit({ folderId: selectedFolderId, name });
      } else if (node) {
        trackSaveAssetUpload(node);
        // 容量校验已移至 saveMediaLibraryItem API 层，超容时会抛出 StorageExceededError
        await saveTeamFlowNodeToMediaLibrary({
          node,
          name,
          folderId: selectedFolderId,
        });
        await loadSubscribeInfo().catch(() => {});
      } else {
        throw new Error(languageStore.smartcatTranslationText((v) => v.no_content_to_save));
      }
      message.success(languageStore.smartcatTranslationText((v) => v.roboneo_web_saved_successfully));
      onClose();
    } catch (err: any) {
      // 容量不足时展示拦截弹窗
      if (err instanceof StorageExceededError) {
        setSaveModalOpen(false);
        // 构造待上传资产信息用于弹窗展示
        const uploadAsset: UploadAssetItem = {
          id: node?.id || 'custom-asset',
          name: name || languageStore.smartcatTranslationText((v) => v.untitled_asset),
          type: getStorageExceededAssetType(previewInfo.icon),
          format: getStorageExceededAssetFormat(previewInfo.icon),
          thumbnail: previewInfo.url || undefined,
          variant: getStorageExceededAssetVariant(previewInfo.icon),
        };
        const payload: AssetStorageExceededPayload = {
          scene: 'save_to_media_library',
          storageInfo: err.storageInfo,
          requiredBytes: err.requiredBytes,
          uploadAssets: [uploadAsset],
        };
        if (assetStorageExceededHandler) {
          assetStorageExceededHandler(payload);
        } else {
          message.error('团队资产空间不足');
        }
        onClose();
        return;
      }
      if (!isAssetLibraryApiMessageShown(err)) {
        message.error(err?.message || languageStore.smartcatTranslationText((v) => v.roboneo_web_saved_failed));
      }
    } finally {
      setSubmitting(false);
    }
  }, [assetName, hideName, languageStore, loadSubscribeInfo, node, onClose, onSubmit, previewInfo, selectedFolderId, submitting]);

  /** 同步 selectedFolderId ref，供异步 reload 保留当前选择。 */
  useEffect(() => {
    selectedFolderIdRef.current = selectedFolderId;
  }, [selectedFolderId]);

  /** 首次展示弹窗时加载文件夹树，触发默认分组懒初始化。 */
  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  /** 点击下拉以外区域时收起分组列表。 */
  useEffect(() => {
    /** 文档点击监听仅保留选择按钮和下拉面板内的点击，点到标签/空白区域时也收起。 */
    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const dropdownRoot = dropdownRef.current;
      const clickedSelect = dropdownRoot?.querySelector(`.${styles.folderSelect}`)?.contains(target);
      const clickedDropdown = dropdownRoot?.querySelector(`.${styles.folderDropdown}`)?.contains(target);
      if (!clickedSelect && !clickedDropdown) {
        setFolderDropdownOpen(false);
        setCreatingParentId(null);
        setCreatingName('');
      }
    };
    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, []);

  return (
    <Modal
      open={saveModalOpen}
      centered
      footer={null}
      closable={false}
      width={560}
      zIndex={zIndex}
      className={styles.saveToMediaLibraryModal}
      onCancel={onClose}
    >
      <div className={styles.modalContent}>
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_sidebar_close)}>
          <IconFont name="crossBlack" fontSize={20} color="currentColor" />
        </button>
        <div className={styles.title}>{languageStore.smartcatTranslationText((v) => v.save_as_asset)}</div>
        <div className={styles.main}>
          <div className={styles.previewBox}>
            {previewInfo.url ? (
              <img src={previewInfo.url} alt="" className={styles.previewImage} crossOrigin="anonymous" />
            ) : (
              <IconFont name={previewInfo.icon} fontSize={36} color="currentColor" />
            )}
          </div>
          <div className={styles.form}>
            {!hideName && (
              <div className={styles.formItem}>
                <span className={styles.label}>{languageStore.smartcatTranslationText((v) => v.asset_name)}</span>
                <Input
                  className={styles.inputBox}
                  inputClassName={styles.input}
                  value={assetName}
                  placeholder={languageStore.smartcatTranslationText((v) => v.enter_name)}
                  maxLength={MAX_NAME_LENGTH}
                  onChange={(event) => setAssetName(truncateByRune(event.target.value))}
                  rightIcon={(
                    <span className={styles.counter}>
                      {`${Array.from(assetName).length}/${MAX_NAME_LENGTH}`}
                    </span>
                  )}
                />
              </div>
            )}
            <div className={styles.formItem} ref={dropdownRef}>
              <span className={styles.label}>{languageStore.smartcatTranslationText((v) => v.save_location)}</span>
              <button
                type="button"
                className={classNames(styles.folderSelect, { [styles.open]: folderDropdownOpen })}
                onClick={() => setFolderDropdownOpen((open) => !open)}
              >
                <span className={selectedFolder ? styles.folderSelectedText : styles.folderPlaceholder}>
                  {folderLoading
                    ? languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_loading1)
                    : selectedFolder?.name
                      || (selectedFolder
                        ? unnamedFolderText
                        : languageStore.smartcatTranslationText((v) => v.please_select_a_folder))}
                </span>
                <IconFont name="chevronDownFill" fontSize={10} color="currentColor" />
              </button>
              {folderDropdownOpen && (
              <div className={styles.folderDropdown}>
                <div className={styles.folderDropdownHeader}>
                  <span>{languageStore.smartcatTranslationText((v) => v.asset_library)}</span>
                  <button
                    type="button"
                    className={styles.createFolderBtn}
                    onClick={() => {
                      setCreatingParentId(0);
                      setCreatingName('');
                    }}
                  >
                    <IconFont name="plusBold" fontSize={14} color="currentColor" />
                    {languageStore.smartcatTranslationText((v) => v.new_folder)}
                  </button>
                </div>
                {creatingParentId === 0 && (
                <div className={styles.createFolderRow}>
                  <input
                    value={creatingName}
                    maxLength={MAX_NAME_LENGTH}
                    placeholder={languageStore.smartcatTranslationText((v) => v.folder)}
                    onChange={(event) => setCreatingName(truncateByRune(event.target.value))}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') submitCreateFolder();
                      if (event.key === 'Escape') {
                        setCreatingParentId(null);
                        setCreatingName('');
                      }
                    }}
                  />
                  <button type="button" onClick={submitCreateFolder} disabled={creating}>
                    {languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm)}
                  </button>
                </div>
                )}
                <div className={styles.folderList}>
                  {flatFolders.length === 0 && (
                  <div className={styles.emptyFolder}>
                    {folderLoading
                      ? languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_loading1)
                      : languageStore.smartcatTranslationText((v) => v.empty)}
                  </div>
                  )}
                  {visibleFolders.map((folder) => {
                    const active = Number(folder.id) === Number(selectedFolderId);
                    const folderId = Number(folder.id);
                    const hasChildren = expandableFolderIds.has(folderId);
                    const expanded = expandedFolderIds.has(folderId);
                    const canCreateChild = Number(folder.depth) < 10;
                    // 叶子节点通过缩进补齐展开按钮列，不渲染空占位块；深层级缩进设上限，保证长名称仍有展示空间。
                    const folderRowPaddingLeft = Math.min(8 + folder.level * 12 + (hasChildren ? 0 : 16), 96);
                    return (
                      <React.Fragment key={folder.id}>
                        <div
                          className={classNames(styles.folderRow, { [styles.active]: active })}
                          style={{ paddingLeft: folderRowPaddingLeft }}
                          title={folder.name || unnamedFolderText}
                        >
                          {hasChildren ? (
                            <button
                              type="button"
                              className={styles.folderExpandBtn}
                              aria-label={expanded ? '收起文件夹' : '展开文件夹'}
                              aria-expanded={expanded}
                              onClick={() => toggleFolderExpanded(folderId)}
                            >
                              <IconFont name={expanded ? 'chevronDownFill' : 'chevronRightBold'} fontSize={8} color="currentColor" />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className={styles.folderSelectBtn}
                            onClick={() => {
                              setSelectedFolderId(Number(folder.id));
                              setFolderDropdownOpen(false);
                            }}
                          >
                            <span className={styles.folderName}>{folder.name || unnamedFolderText}</span>
                          </button>
                          {canCreateChild && (
                          <button
                            type="button"
                            className={styles.folderAddChild}
                            onClick={(event) => {
                              event.stopPropagation();
                              setCreatingParentId(Number(folder.id));
                              setCreatingName('');
                            }}
                          >
                            <IconFont name="plusBold" fontSize={14} color="currentColor" />
                          </button>
                          )}
                          <span className={styles.folderCheck}>
                            {active && <IconFont name="checkV2Bold" fontSize={14} color="currentColor" />}
                          </span>
                        </div>
                        {creatingParentId === Number(folder.id) && (
                        <div className={styles.createFolderRow} style={{ marginLeft: 22 + folder.level * 12 }}>
                          <input
                            value={creatingName}
                            maxLength={MAX_NAME_LENGTH}
                            placeholder={languageStore.smartcatTranslationText((v) => v.folder)}
                            onChange={(event) => setCreatingName(truncateByRune(event.target.value))}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') submitCreateFolder();
                              if (event.key === 'Escape') {
                                setCreatingParentId(null);
                                setCreatingName('');
                              }
                            }}
                          />
                          <button type="button" onClick={submitCreateFolder} disabled={creating}>
                            {languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm)}
                          </button>
                        </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <Button type="plain" size="large" className={styles.cancelBtn} onClick={onClose}>
            {languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_cancel)}
          </Button>
          <Button
            type="default"
            size="large"
            className={classNames(styles.saveBtn, { [styles.saveBtnDisabled]: saveDisabled })}
            disabled={saveDisabled}
            onClick={submitSave}
          >
            {languageStore.smartcatTranslationText((v) => v.roboneo_web_save)}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** 统一静态弹窗挂载和销毁逻辑，避免两个入口重复维护临时容器。 */
function renderStaticSaveToMediaLibraryModal(renderModal: (onClose: () => void) => React.ReactElement): Promise<void> {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    /** 关闭静态弹窗时销毁临时挂载点，保证外部菜单卸载后弹窗仍可自行收尾。 */
    const cleanup = () => {
      // eslint-disable-next-line react/no-deprecated
      ReactDOM.unmountComponentAtNode(container);
      container.remove();
      resolve();
    };
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.render(renderModal(cleanup), container);
  });
}

/** 以静态方法展示保存弹窗，避免右键菜单销毁时卸载弹窗。 */
export function showSaveToMediaLibraryModal(node: FlowNodeEntity): Promise<void> {
  return renderStaticSaveToMediaLibraryModal((onClose) => (
    <SaveToMediaLibraryModal node={node} onClose={onClose} />
  ));
}

/** 以静态方法展示通用保存弹窗，供非 FlowNode 资源复用现有资产库保存 UI。 */
export function showMediaLibrarySaveModal(options: {
  initialName: string;
  onSubmit: (submitOptions: { folderId: number; name: string; }) => Promise<void>;
  previewInfo: { icon: string; url: string; };
  initialFolderSystemKey?: MediaLibrarySystemKey;
  zIndex?: number;
}): Promise<void> {
  const {
    initialName,
    initialFolderSystemKey,
    onSubmit,
    previewInfo,
    zIndex,
  } = options;
  return renderStaticSaveToMediaLibraryModal((onClose) => (
    <SaveToMediaLibraryModal
      initialName={initialName}
      initialFolderSystemKey={initialFolderSystemKey}
      onClose={onClose}
      onSubmit={onSubmit}
      previewInfo={previewInfo}
      zIndex={zIndex}
    />
  ));
}

/**
 * 以静态方法展示多选批量保存弹窗：隐藏名称字段（各资源默认使用节点名称），
 * 用户仅选择保存位置，确认后由 onSubmit 批量保存全部资源。
 */
export function showBatchSaveToMediaLibraryModal(options: {
  onSubmit: (submitOptions: { folderId: number; }) => Promise<void>;
  previewInfo?: { icon: string; url: string; };
  initialFolderSystemKey?: MediaLibrarySystemKey;
  zIndex?: number;
}): Promise<void> {
  const {
    onSubmit,
    previewInfo = { icon: 'fileBold', url: '' },
    initialFolderSystemKey,
    zIndex,
  } = options;
  return renderStaticSaveToMediaLibraryModal((onClose) => (
    <SaveToMediaLibraryModal
      hideName
      initialFolderSystemKey={initialFolderSystemKey}
      onClose={onClose}
      onSubmit={({ folderId }) => onSubmit({ folderId })}
      previewInfo={previewInfo}
      zIndex={zIndex}
    />
  ));
}

export default SaveToMediaLibraryModal;
